import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { FloatingPortal, flip, offset, shift, useFloating } from '@floating-ui/react';
import { Extension } from '@tiptap/core';
import Blockquote from '@tiptap/extension-blockquote';
import Code from '@tiptap/extension-code';
import Document from '@tiptap/extension-document';
import Link from '@tiptap/extension-link';
import { BulletList, ListItem, OrderedList } from '@tiptap/extension-list';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { Placeholder, UndoRedo } from '@tiptap/extensions';
import { DOMParser, Slice } from '@tiptap/pm/model';
import { EditorContent, useEditor } from '@tiptap/react';
import { useMediaQuery } from '@uidotdev/usehooks';
import { useClickAway } from '@uidotdev/usehooks';
import { Laugh, Paperclip, X } from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';

import { BREAKPOINT_SM } from '../lib/breakpoints';
import { clearDraft, setDraft, useDraft } from '../lib/draft_store';
import { ensureEmojiCache } from '../lib/emojiCache';
import Emoji from '../lib/extension-emoji/index';
import { gifFullFile } from '../lib/klipy';
import { logDebug, logError, logWarn } from '../lib/logger';
import {
  MediaAttachmentOwnerTypes,
  enqueueUpload,
  getMediaAttachmentsForSession,
  isComposerOwnedMediaAttachment,
  removeEditMediaAttachmentsForChannel,
  removeMediaAttachmentsForSessionKey,
  sortByTimestamp,
  syncExistingMediaAttachments,
  useAllMediaAttachments,
} from '../lib/media_attachments_store';
import { exceedsRichdownContentSize } from '../lib/richdownContentSize';
import { Channel, CustomBold, CustomCodeBlock, CustomEnter, CustomItalic, CustomStrike, Profile, Spammer } from '../lib/tiptap-extensions';
import { isEditorContentEmpty, isEditorViewMounted } from '../lib/tiptapEditor';
import { cn } from '../lib/utils';

import SendMessageIcon from '../../assets/images/send-message.svg';

import { KEYCHAIN, useRegisterWithKeyMaster } from '../contexts/KeyMaster';
import { useRichdownComposerFocusRouter } from '../contexts/RichdownComposerFocusRouter';
import { useMessageAuthor } from '../hooks/useMessageAuthor';
import ComposerSelectionToolbar from './ComposerSelectionToolbar';
import EmojibarPopover from './EmojibarPopover';
import RichdownComposerMediaAttachments from './RichdownComposerMediaAttachments';
import createChannelSuggestion from './extensions/channelSuggestion';
import createEmojiSuggestion from './extensions/emojiSuggestion';
import createProfileSuggestion from './extensions/profileSuggestion';
import { MESSAGE_CONTENT_TYPOGRAPHY_CLASS } from './messages/Message';

// Detects if pasted HTML has code-like styling (from IDEs like VS Code)
// TODO: Consider treating <tt> as inline code on paste if needed.
const looksLikeCodePaste = (html) => {
  if (!html) return false;
  // True code blocks should use <pre>.
  if (/<pre\b/i.test(html)) return true;

  // Some IDEs (and macOS) wrap code in <div style="white-space: pre"> with monospace.
  // Avoid matching list content (e.g., GitHub list items with inline <code>).
  const hasPreStyle = /white-space:\s*pre\b/i.test(html);
  const hasMonospace = /monospace/i.test(html);
  const hasListMarkup = /<(ul|ol|li)\b/i.test(html);
  return hasPreStyle && hasMonospace && !hasListMarkup;
};

// Checks if pasted text is a single line (no newlines)
const isSingleLinePaste = (plainText) => !plainText.includes('\n');

// Strips white-space: pre* styles from non-<pre> elements to prevent
// false code block detection (e.g., Twitter URLs have white-space: pre-wrap)
const sanitizePastedHtml = (html) => {
  return html.replace(/white-space:\s*pre[^;"]*/gi, '');
};

// Detects if pasted HTML looks like a styled URL from Twitter/X
// (a span with pre-wrap styling containing just a URL)
const looksLikeTwitterUrlPaste = (html, plainText) => {
  if (!html || !plainText) return false;
  const isUrl = /^https?:\/\/\S+$/.test(plainText.trim());
  const hasPreWrap = /white-space:\s*pre-wrap/i.test(html);
  const isSpanWrapper = /<span[^>]*>/.test(html) && !/<(pre|code|div|p)\b/i.test(html);
  return isUrl && hasPreWrap && isSpanWrapper;
};

const pastedHtmlExists = (html) => !!html;

// HTML paste UX note:
// ProseMirror can leave inline marks "open" after paste (especially links, but also bold/italic/strike/code),
// which makes the next typed character keep that formatting. That's usually unwanted in chat.
// So after HTML paste we close those common marks and add one trailing space only when we
// actually removed one of those marks. This avoids mutating block-style content (e.g. code blocks).
const MARKS_TO_CLOSE_AFTER_HTML_PASTE = new Set(['link', 'bold', 'italic', 'strike', 'code']);

const getActiveMarksAtSelection = (state) => state?.storedMarks ?? state?.selection?.$from?.marks() ?? [];

const getMarksAfterHtmlPaste = (state) => {
  if (!state) return [];

  const activeMarks = getActiveMarksAtSelection(state);
  return activeMarks.filter((mark) => !MARKS_TO_CLOSE_AFTER_HTML_PASTE.has(mark.type.name));
};

// Runs right after paste finishes, then nudges cursor state into a more chat-friendly default.
const normalizeAfterHtmlPaste = (view) => {
  const { state, dispatch } = view || {};
  const { selection } = state || {};
  const { $from } = selection || {};
  const parent = $from?.parent;
  if (!state || !dispatch || !selection?.empty || !parent?.inlineContent || parent.type.name === 'codeBlock') return;

  const activeMarks = getActiveMarksAtSelection(state);
  const marksAfterHtmlPaste = getMarksAfterHtmlPaste(state);
  const didCloseInlineMark = marksAfterHtmlPaste.length !== activeMarks.length;
  if (!didCloseInlineMark) return;

  const tr = state.tr.setStoredMarks(marksAfterHtmlPaste);
  tr.insertText(' ', selection.from);
  dispatch(tr);
};

const scheduleHtmlPasteNormalization = (view) => {
  const run = () => normalizeAfterHtmlPaste(view);
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(run);
    return;
  }
  setTimeout(run, 0);
};

function hasEditorContent(editor) {
  return !isEditorContentEmpty(editor);
}

// ─────────────────────────────────────────────────────────────────────────────
// Should Enter key add a newline instead of submitting?
// ─────────────────────────────────────────────────────────────────────────────
// When cursor is in a structural node (code block, blockquote),
// Enter should add a newline, not submit the message.
// Lists are deliberately excluded: Enter sends the message as-is and Shift-Enter
// is what moves to the next bullet (see CustomEnter).
// This ONLY affects Enter key behavior - Send button always submits.
function shouldEnterAddNewline(editor) {
  if (!editor) return false;

  const { $from } = editor.state.selection;
  const parentNodeType = $from.parent.type.name;

  if (parentNodeType === 'codeBlock') return true;
  if (editor.isActive('blockquote')) return true;

  return false;
}

// Shift-Enter opens the next bullet, so sending right after it would ship an empty
// one. Drops that trailing item; a list with nothing else in it is empty content
// and never reaches submit.
function trimTrailingEmptyListItem(editor) {
  const { doc } = editor.state;
  const list = doc.lastChild;

  if (!list || (list.type.name !== 'bulletList' && list.type.name !== 'orderedList')) return;
  if (list.childCount < 2) return;
  if (list.lastChild.textContent.trim() !== '') return;

  // The list is the doc's last child, so it closes at the end of the doc content.
  const itemEnd = doc.content.size - 1;
  editor.view.dispatch(editor.state.tr.delete(itemEnd - list.lastChild.nodeSize, itemEnd));
}

// ListItem binds Enter to splitListItem, and its keymap runs before CustomEnter's,
// so it would swallow Enter inside a list before the composer can submit.
// Declining the key here lets Enter fall through to CustomEnter.
const SubmitOnEnterListItem = ListItem.extend({
  addKeyboardShortcuts() {
    return {
      ...this.parent?.(),
      Enter: () => false,
    };
  },
});

export const RichdownButton = ({ editor, onClick, disabled = false, className, ariaLabel, children }) => (
  <button
    type="button"
    className={`flex aspect-square size-9 cursor-pointer items-center justify-center rounded-full p-1 ${className || ''}`}
    onClick={(event) => {
      if (onClick) onClick(event);
      if (editor) editor.commands.focus();
    }}
    disabled={disabled}
    aria-label={ariaLabel}
  >
    {children}
  </button>
);

// The emoji and attachment buttons inline at the left edge of the composer
const ComposerActions = ({ setEmojiButtonRef, editorId, onEmojibarToggle, onAttachmentClick, isAtMaxCapacity }) => (
  <div className="flex items-center">
    <button
      ref={setEmojiButtonRef}
      className="composer-action-button flex size-9 cursor-pointer touch-manipulation items-center justify-center rounded-full text-gray-600 duration-75 **:transition-none hover:bg-black/5 hover:text-black focus-visible:bg-black/5 dark:text-gray-100 dark:hover:bg-black/20 dark:hover:text-white dark:focus-visible:bg-black/20"
      data-editor-id={editorId}
      onClick={onEmojibarToggle}
      aria-label="Add Emoji"
      type="button"
    >
      <Laugh className="size-4.5" strokeWidth={2} />
    </button>

    <button
      className="composer-action-button mr-0.5 -ml-0.5 flex size-9 cursor-pointer touch-manipulation items-center justify-center rounded-full text-gray-600 duration-75 **:transition-none hover:bg-black/5 hover:text-black focus-visible:bg-black/5 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-100 dark:hover:bg-black/20 dark:hover:text-white dark:focus-visible:bg-black/20"
      onClick={onAttachmentClick}
      disabled={isAtMaxCapacity}
      aria-label="Add attachment"
      type="button"
    >
      <Paperclip className="size-4.5" strokeWidth={2} />
    </button>
  </div>
);

// Keyboard handler for close buttons (Enter/Space triggers the action)
const createCloseButtonKeyHandler = (handler) => (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handler();
  }
};

// Figma composer/inputs edit row. Layout only — surface colors, hairlines, and the
// BG-image glass variants live in application.css (composer-mode-bar rules), so they
// can be overridden per composer mode from one CSS layer.
// pl nudged +2px over pr so the label's optical start lines up with the content below
const COMPOSER_MODE_BAR_CLASS_NAME = 'composer-mode-bar flex h-11 items-center justify-between gap-3 rounded-t-3xl pr-3.5 pl-4';
// The Add Members checkbox circle spec (size-5, 12px lucide glyph @ 1.5), on a dark chip.
const COMPOSER_MODE_BAR_CLOSE_CLASS_NAME =
  'composer-mode-bar-close flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none';

// The edit and reply banners above the input share one anatomy: label + close chip.
const ComposerModeBar = ({ label, onClose, closeAriaLabel }) => (
  <div className={COMPOSER_MODE_BAR_CLASS_NAME}>
    <span className="min-w-0 truncate text-cursed/5 font-medium">{label}</span>
    <button
      type="button"
      className={COMPOSER_MODE_BAR_CLOSE_CLASS_NAME}
      onClick={onClose}
      onKeyDown={createCloseButtonKeyHandler(onClose)}
      aria-label={closeAriaLabel}
    >
      <X size={12} strokeWidth={1.5} absoluteStrokeWidth />
    </button>
  </div>
);

const RichdownComposer = forwardRef(
  (
    {
      initialContent = '',
      onChange,
      onSubmit,
      onEmptyStateChange,
      placeholder = 'Message',
      className = '',
      editorId = 'default',
      hideToolbar = false,
      // Trims the row's gutters without touching the 36px send button, so a surface that
      // is short on vertical room gets a shorter composer rather than a different one.
      dense = false,
      variant,
      onReady = null,
      editingMessageId = null,
      onCancelEditing = null,
      editingMessage = null,
      onClearAttachments = null,
      dropzoneHelpers = null,
      onDropzoneStateChange = null,
      onUploadCountChange = null,
      maxFiles = 0,
      maxRichdownContentSize = 0,
      submitButton = null,
      containerClassName = null,
      disabled = false,
      replyToMessageId = null,
      replyToMessage = null,
      onClearReply = null,
      channelId = null,
      workspaceId = null,
      mainChannelId = null,
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isEmojibarOpen, setIsEmojibarOpen] = useState(false);
    const [attachmentLimitExceeded, setAttachmentLimitExceeded] = useState(false);
    const isBreakpointSm = useMediaQuery(BREAKPOINT_SM);

    const { refs: emojibarRefs, floatingStyles: emojibarFloatingStyles } = useFloating({
      open: isEmojibarOpen,
      placement: 'top',
      // Free-floating against the viewport; top padding keeps it out of the
      // draggable title-bar zone, where it couldn't be clicked.
      middleware: [offset({ mainAxis: 20, crossAxis: 20 }), flip(), shift({ padding: { top: 48, left: 8, right: 8, bottom: 8 } })],
    });

    const emojibarContentRef = useClickAway(() => {
      setIsEmojibarOpen(false);
    });
    // Opening the Add Emoji modal (from the picker's bottom bar) dismisses the picker itself.
    useEffect(() => {
      const closeEmojibarForAddEmojiModal = () => setIsEmojibarOpen(false);
      window.addEventListener('output:internal:add-emoji-modal', closeEmojibarForAddEmojiModal);
      return () => window.removeEventListener('output:internal:add-emoji-modal', closeEmojibarForAddEmojiModal);
    }, []);

    const { register, unregister, setActiveComposer, focusChannelComposer } = useRichdownComposerFocusRouter();
    // Disable draft persistence when editing a message
    const draftPersistenceId = editingMessageId ? '' : editorId;
    const savedDraftContent = useDraft(draftPersistenceId);
    // tracks channel switches for draft loading
    const previousPersistenceIdRef = useRef(null);
    // Stable draft session key for this composer. Optimistic handoff transfers rows
    // off the composer, so the draft session can survive across sends/reloads.
    const baseSessionKey = React.useMemo(() => `richdown-composer:${editorId}`, [editorId]);
    // Which upload rows should the UI show right now?
    // Points at the draft session normally, or the edit session when editing.
    const [activeSessionKey, setActivesessionKey] = useState(() => baseSessionKey);
    // Holds the temporary edit session key when editing a message
    // Used for cleanup when editing ends (cancel, switch messages, unmount)
    const activeEditSessionKeyRef = useRef(null);
    const composeSessionKeyRef = useRef(baseSessionKey);
    // Real-time mirror of activeSessionKey for non-React contexts
    // (file inputs, async handlers) that need current value without state delays
    const activeSessionKeyRef = useRef(baseSessionKey);
    // Detects when editorId changes to trigger full reset and cleanup of old instance
    const previousBasesessionKeyRef = useRef(baseSessionKey);
    // Placeholder text can change without remounting this component (e.g., channel rename).
    // Keep it in a ref so the Placeholder extension can read the latest value.
    const placeholderTextRef = useRef(placeholder);

    // The tiptap editor captures extension config at creation time, so we mirror
    // the channelId prop into a ref and let the profile suggestion factory read
    // through it — otherwise the suggestion would be frozen to the initial value.
    const channelIdRef = useRef(channelId);
    const workspaceIdRef = useRef(workspaceId);
    const mainChannelIdRef = useRef(mainChannelId);

    // Timestamp for uniquness between edit sessions
    const generateEditSessionKey = useCallback((messageId) => `${baseSessionKey}:edit-${messageId}-${Date.now()}`, [baseSessionKey]);

    const draftOwnerOptions = useMemo(
      () => (!editingMessageId ? { ownerType: MediaAttachmentOwnerTypes.composer, ownerId: editorId } : {}),
      [editingMessageId, editorId],
    );

    // removeMediaAttachmentsForSessionKey cascades through pending uploads so nothing lingers once the key goes inactive
    const cleanupUploadsForSessionKey = useCallback((sessionKey) => {
      if (!sessionKey) return;
      removeMediaAttachmentsForSessionKey(sessionKey).catch((error) => {
        logWarn('RichdownComposer', 'Failed to cleanup uploads for session key', { sessionKey, error });
      });
    }, []);

    const handleCancelEditing = useCallback(() => {
      const sessionKey = activeEditSessionKeyRef.current;
      if (sessionKey) cleanupUploadsForSessionKey(sessionKey);
      if (onCancelEditing) onCancelEditing();
    }, [cleanupUploadsForSessionKey, onCancelEditing]);

    // When the base composer key changes (e.g., new editorId) reset to the new context
    // Cancels edit mode if active, clears lingering edit uploads, and realigns refs/state to the new base key
    useEffect(() => {
      // No matter what happens, if this is not in edit mode, we can take this as a chance
      // to delete any dexie uploads for this channel that were edit mode-specific,
      // in case any are lingering (like what might happen if you just reload the page while editing)

      if (!activeEditSessionKeyRef.current) {
        removeEditMediaAttachmentsForChannel(editorId).catch((error) => {
          logWarn('RichdownComposer', 'Failed to cleanup edit uploads for channel', { editorId, error });
        });
      }

      const previousBaseKey = previousBasesessionKeyRef.current;
      if (previousBaseKey === baseSessionKey) return;
      previousBasesessionKeyRef.current = baseSessionKey;
      composeSessionKeyRef.current = baseSessionKey;

      if (editingMessageId) {
        handleCancelEditing();
        return;
      }

      const previousSessionKey = activeEditSessionKeyRef.current;
      if (previousSessionKey) cleanupUploadsForSessionKey(previousSessionKey);
      activeEditSessionKeyRef.current = null;
      activeSessionKeyRef.current = baseSessionKey;
      setActivesessionKey(baseSessionKey);
    }, [baseSessionKey, cleanupUploadsForSessionKey, editingMessageId, editorId, handleCancelEditing]);

    // Just a mirror of activeSessionKey for non-React contexts
    activeSessionKeyRef.current = activeSessionKey;
    composeSessionKeyRef.current = baseSessionKey;
    placeholderTextRef.current = placeholder;
    channelIdRef.current = channelId;
    workspaceIdRef.current = workspaceId;
    mainChannelIdRef.current = mainChannelId;

    useEffect(
      () => () => {
        const sessionKey = activeEditSessionKeyRef.current;
        if (sessionKey) cleanupUploadsForSessionKey(sessionKey);
      },
      [cleanupUploadsForSessionKey],
    );
    const uploadsMap = useAllMediaAttachments({ channel_id: editorId });

    const composerMediaAttachments = React.useMemo(() => {
      if (!(uploadsMap instanceof Map)) return [];

      return Array.from(uploadsMap.values())
        .filter((upload) => {
          if (!upload) return false;
          if (!isComposerOwnedMediaAttachment(upload)) return false;
          if (upload.session_key !== activeSessionKey) return false;
          return true;
        })
        .sort(sortByTimestamp('written_at'));
    }, [uploadsMap, activeSessionKey]);

    const uploadsCount = composerMediaAttachments.length;
    const hasUploads = uploadsCount > 0;

    // Notify parent when upload count changes
    useEffect(() => {
      if (onUploadCountChange) {
        onUploadCountChange(uploadsCount);
      }
    }, [uploadsCount, onUploadCountChange]);

    // Clear attachment limit exceeded error when any attachment is removed
    const previousUploadsCountRef = useRef(uploadsCount);
    useEffect(() => {
      if (uploadsCount < previousUploadsCountRef.current) {
        setAttachmentLimitExceeded(false);
      }
      previousUploadsCountRef.current = uploadsCount;
    }, [uploadsCount]);

    const handleDismissAttachmentLimit = useCallback(() => {
      setAttachmentLimitExceeded(false);
    }, []);

    // NOTE: hasIncompleteUploads removed - restore if needed for upload validation

    const [hasTextContent, setHasTextContent] = useState(false);
    const [isRichdownContentTooLarge, setIsRichdownContentTooLarge] = useState(false);

    // Tiptap's CustomEnter extension grabs the submit callback once on mount.
    // Without a ref it keeps calling the original handler, so channel changes
    // never reach it. Keeping the latest onSubmit in submitHandlerRef avoids
    // that stale closure.
    const submitHandlerRef = useRef(onSubmit);
    // Same comment as above, but for draftPersistenceId
    const draftPersistenceIdRef = useRef(draftPersistenceId);
    // ref for onCreate callback
    const savedDraftContentRef = useRef(savedDraftContent);
    // Same for ableToSend - keep the latest value in a ref for enterKeyHandler
    const ableToSendRef = useRef(false);
    // Same for performSubmission - TipTap captures enterKeyHandler once at init, so
    // performSubmission must be read via ref to avoid stale editingMessageId/draftOwnerOptions
    const performSubmissionRef = useRef(null);
    // Detects when switching between different messages being edited
    const previousEditingMessageIdRef = useRef(null);
    // Tracks the actual message object being edited to detect content changes
    const previousEditingMessageRef = useRef(null);
    // Tracks when attachments have been prepopulated to avoid duplicate enqueueing
    const lastPrepopulatedAttachmentsRef = useRef({ sessionKey: null, signature: null });
    // Ref for remainingAttachmentSlots to access current value in paste handler (avoids stale closure)
    const remainingAttachmentSlotsRef = useRef(undefined);

    // Bundles editing/reply state for the Escape key handler to access current values
    const editingStateRef = useRef({
      editingMessageId,
      handleCancelEditing,
      hasUploads: false,
      replyToMessageId,
      onClearReply,
      composeSessionKey: baseSessionKey,
      cleanupUploadsForSessionKey,
    });

    // Packages us some state so we can use it in the tiptap escape handler
    useEffect(() => {
      editingStateRef.current = {
        editingMessageId,
        handleCancelEditing,
        hasUploads,
        replyToMessageId,
        onClearReply,
        composeSessionKey: baseSessionKey,
        cleanupUploadsForSessionKey,
      };
    }, [editingMessageId, handleCancelEditing, hasUploads, replyToMessageId, onClearReply, baseSessionKey, cleanupUploadsForSessionKey]);

    // TipTap manages focus inside a contenteditable element; react-hotkeys-hook loses the event when the editor steals focus,
    // so we register our own shortcuts directly on the editor via a custom extensions instead.
    const escapeKeyExtension = useMemo(
      () =>
        Extension.create({
          name: 'customEscapeExtension',
          addKeyboardShortcuts() {
            return {
              Escape: ({ editor }) => {
                const {
                  editingMessageId: activeEditingId,
                  handleCancelEditing: cancelEditing,
                  replyToMessageId: activeReplyId,
                  onClearReply: clearReply,
                  composeSessionKey: sessionKey,
                  cleanupUploadsForSessionKey: cleanupUploads,
                } = editingStateRef.current || {};

                // Edit mode takes priority over reply mode
                if (activeEditingId) {
                  cancelEditing?.();
                  return true;
                }

                // Clear reply if in reply mode
                if (activeReplyId) {
                  editor?.commands.clearContent();
                  if (sessionKey) cleanupUploads?.(sessionKey);
                  clearReply?.();
                  return true;
                }

                return false;
              },
            };
          },
        }),
      [],
    );

    const upKeyExtension = useMemo(
      () =>
        Extension.create({
          name: 'customUpKeyExtension',
          addKeyboardShortcuts() {
            return {
              ArrowUp: ({ editor }) => {
                // only run if we are in channel mode
                if (variant !== 'channel') return false;

                const { editingMessageId: activeEditingId, hasUploads: hasAttachments } = editingStateRef.current || {};

                // only run if we aren't editing
                if (activeEditingId) return false;

                // only run if the composer editor is "really empty" (no text and no attachments)
                if (hasEditorContent(editor) || hasAttachments) return false;

                logDebug('RichdownComposer', 'editing recent message via up key extension');
                window.dispatchEvent(new CustomEvent('output:internal:edit-recent-message', { detail: { editorId } }));

                return true;
              },
            };
          },
        }),
      [variant, editorId],
    );

    // Ensure emoji cache is connected to Dexie early for render-time lookups
    useEffect(() => {
      ensureEmojiCache();
    }, []);

    const remainingAttachmentSlots = useMemo(() => {
      if (maxFiles == null) return undefined;
      return Math.max(maxFiles - uploadsCount, 0);
    }, [maxFiles, uploadsCount]);

    const isAtMaxCapacity = useMemo(() => {
      if (remainingAttachmentSlots === undefined) return false;
      return remainingAttachmentSlots === 0;
    }, [remainingAttachmentSlots]);

    const handleAttachmentButtonClick = useCallback(
      (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        if (dropzoneHelpers?.open) {
          dropzoneHelpers.open();
        }
      },
      [dropzoneHelpers],
    );

    const handleEmojibarToggle = useCallback(() => {
      setIsEmojibarOpen((prev) => !prev);
    }, []);

    // Register emojibar with KeyMaster when open
    const emojibarKeychainId = isEmojibarOpen ? KEYCHAIN.POPOVER__COMPOSER_EMOJIBAR : null;
    const { unlocked: emojibarUnlocked } = useRegisterWithKeyMaster(emojibarKeychainId);

    // Close emojibar on Escape key (using KeyMaster pattern)
    useHotkeys(
      'escape',
      () => {
        if (emojibarUnlocked()) {
          setIsEmojibarOpen(false);
        }
      },
      { enabled: isEmojibarOpen, enableOnFormTags: true, preventDefault: true },
      [emojibarUnlocked],
    );

    const handleEmojibarSelect = useCallback(
      (emoji, options = {}) => {
        const editorEvent = new CustomEvent('richdown-composer:command', {
          detail: {
            action: 'insertEmoji',
            options: {
              emoji: emoji.native || null,
              name: emoji.shortcode,
              custom: emoji.kind === 'custom',
              image: emoji.public_url,
            },
            targetEditorId: editorId,
          },
        });

        window.dispatchEvent(editorEvent);

        if (!options.keepOpen) {
          setIsEmojibarOpen(false);
        }
      },
      [editorId],
    );

    // Clicking a GIF sends it immediately as its own attachment-only message.
    // The GIF goes through the normal upload pipeline under a throwaway session
    // key, so the composer's typed draft and staged attachments are untouched.
    const handleGifSelect = useCallback(
      async (gif) => {
        setIsEmojibarOpen(false);
        if (editingMessageId) return;

        const file = gifFullFile(gif);
        if (!file?.url) return;

        const sessionKey = `${editorId}:gif-send-${Date.now()}`;
        try {
          const response = await fetch(file.url);
          if (!response.ok) throw new Error(`GIF download failed with status ${response.status}`);
          const blob = await response.blob();
          const gifFile = new File([blob], `${gif.slug || 'gif'}.gif`, { type: blob.type || 'image/gif' });
          const purpose = editorId?.startsWith('agent-chat-') ? 'agent_chat_message_attachments' : 'message_attachments';

          await enqueueUpload({ file: gifFile, purpose, workspaceId, sessionKey, channelId: editorId, ...draftOwnerOptions });
          const attachments = await getMediaAttachmentsForSession({
            sessionKey,
            channelId: editorId,
            uploadState: null,
            ...draftOwnerOptions,
          });
          await Promise.resolve(submitHandlerRef.current?.('', attachments || []));
        } catch (error) {
          logError('RichdownComposer', 'Failed to send GIF', { editorId, error });
        }
      },
      [draftOwnerOptions, editingMessageId, editorId, workspaceId],
    );

    // ─────────────────────────────────────────────────────────────────────────
    // Perform the actual submission
    // ─────────────────────────────────────────────────────────────────────────
    // Handles the autolink trick (insert/delete space to trigger URL detection)
    // then submits content with attachments and clears the editor.
    const performSubmission = useCallback(
      (_editor) => {
        // Clear attachment limit exceeded error on submit
        setAttachmentLimitExceeded(false);

        const doSubmit = async () => {
          const content = _editor.getHTML();
          const shouldKeepMessageOwnedAttachments = variant === 'channel' && !editingMessageId;
          const submissionUploadState = shouldKeepMessageOwnedAttachments ? null : 'completed';

          // Channel sends keep unfinished upload rows alive for the optimistic message.
          // Edit/non-channel sends only grab fully resolved attachments.
          const attachments = await getMediaAttachmentsForSession({
            sessionKey: activeSessionKeyRef.current,
            channelId: editorId,
            uploadState: submissionUploadState,
            ...draftOwnerOptions,
          });
          // Let the controller persist the optimistic row before we clear local state.
          const submitted = await Promise.resolve(submitHandlerRef.current?.(content, attachments || []));
          // Callers that can observe delivery return false on failure. Keep the draft and
          // attachments in place so retrying does not require reconstructing the message;
          // legacy handlers return undefined and retain the existing clear-on-submit flow.
          if (submitted === false) return;

          if (!shouldKeepMessageOwnedAttachments || (attachments || []).length === 0) {
            onClearAttachments?.((attachments || []).map((attachment) => attachment.attachment_id));
          }

          // Don't reset position when editing
          if (!editingMessageId) {
            if (draftPersistenceIdRef.current) {
              clearDraft(draftPersistenceIdRef.current);
            }
            _editor.commands.clearContent();
            _editor.commands.setTextSelection({ from: 0, to: 0 });
          }
        };

        trimTrailingEmptyListItem(_editor);

        // Autolink trick: insert a space to trigger URL detection, then remove it.
        // Without this, typing a URL and immediately pressing Enter won't autolink.
        // It goes in at the cursor, which is where the URL was just typed: the end of
        // the doc can sit between list items, where the space would open a new bullet
        // that the cleanup below then fails to find.
        const { state, dispatch } = _editor.view;
        const insertPos = state.selection.from;

        const insertTr = state.tr.insertText(' ', insertPos);
        dispatch(insertTr);

        setTimeout(() => {
          const { state: currentState, dispatch: currentDispatch } = _editor.view;
          const docSize = currentState.doc.content.size;

          // Clean up the space we inserted (if it's still there)
          if (insertPos < docSize) {
            const deleteTo = Math.min(insertPos + 1, docSize);
            if (insertPos < deleteTo) {
              const textAtPosition = currentState.doc.textBetween(insertPos, deleteTo, '\n', '\n');
              if (textAtPosition === ' ') {
                const deleteTr = currentState.tr.delete(insertPos, deleteTo);
                currentDispatch(deleteTr);
              }
            }
          }

          doSubmit().catch((error) => {
            logWarn('RichdownComposer', 'Failed to submit composer content', { editorId, error });
          });
        }, 10);
      },
      [draftOwnerOptions, editorId, editingMessageId, onClearAttachments, variant],
    );

    // ─────────────────────────────────────────────────────────────────────────
    // Enter key behavior
    // ─────────────────────────────────────────────────────────────────────────
    // In structural nodes (code block, blockquote): Enter adds newline
    // Otherwise (including lists): Enter submits if able, or blocks if not
    const enterKeyHandler = (_editor) => {
      // Structural nodes: let Tiptap handle Enter as a newline
      if (shouldEnterAddNewline(_editor)) return false;

      // Can't submit: block Enter entirely (no newline, no submit)
      if (!ableToSendRef.current) return true;

      performSubmissionRef.current?.(_editor);
      return true;
    };

    const placeholderExtension = useMemo(
      () =>
        Placeholder.configure({
          placeholder: () => placeholderTextRef.current,
        }),
      [],
    );

    const editor = useEditor({
      autofocus: false,
      extensions: [
        Document,
        Paragraph,
        Text,
        UndoRedo,
        CustomBold,
        CustomItalic,
        CustomStrike,
        Code,
        Blockquote,
        Channel.configure({
          suggestion: createChannelSuggestion({
            getWorkspaceId: () => workspaceIdRef.current,
            getMainChannelId: () => mainChannelIdRef.current,
          }),
        }),
        Profile.configure({
          // Pass a getter (not the raw value) so suggestion lookups read the
          // latest channelId even though useEditor only initializes once.
          suggestion: createProfileSuggestion({ getChannelId: () => channelIdRef.current, getWorkspaceId: () => workspaceIdRef.current }),
        }),
        Spammer,
        CustomEnter.configure({
          enterKeyHandler: enterKeyHandler,
        }),
        CustomCodeBlock.configure({
          exitOnTripleEnter: true,
        }),
        BulletList,
        OrderedList,
        SubmitOnEnterListItem,
        Link.configure({
          openOnClick: false,
          protocols: ['http', 'https'],
          defaultProtocol: 'https',
          autolink: true,
          // TipTap's default isAllowedUri has a regex bug where the character class
          // [^a-z+.-:] accidentally includes "/" due to the range from "." to ":".
          // This causes URLs with paths like "example.com/page" to fail validation.
          // We override it to trust linkifyjs's validation (which already runs in autolink).
          isAllowedUri: (url, ctx) => {
            // Use the default validation for protocol-prefixed URLs
            if (ctx.defaultValidate(url)) return true;
            // For protocol-less URLs, allow if they look like valid domains with optional paths
            // This regex allows: domain.tld, domain.tld/path, domain.tld/path?query, etc.
            return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+([/?#].*)?$/i.test(url);
          },
        }),
        placeholderExtension,
        Emoji.configure({
          suggestion: {
            ...createEmojiSuggestion(),
            char: ':',
            startOfLine: false,
            allowSpaces: false,
            triggerOnColon: false,
          },
        }),
        escapeKeyExtension,
        upKeyExtension,
      ],
      content: initialContent,
      onFocus: () => setIsFocused(true),
      onBlur: () => setIsFocused(false),
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        const isEmpty = isEditorContentEmpty(editor);
        const hasContent = !isEmpty;

        // check byte size of richdown content
        setIsRichdownContentTooLarge(exceedsRichdownContentSize(html, maxRichdownContentSize));

        setHasTextContent(hasContent);
        onChange?.(html);
        onEmptyStateChange?.(isEmpty);
        updateToolbarState(editor);
        // draft saving done via editor.on('update') to avoid stale closures
      },
      onCreate: ({ editor }) => {
        // Initialize hasTextContent state
        setHasTextContent(hasEditorContent(editor));

        // Skip autofocus on mobile - it can trigger unwanted scroll behavior
        if (variant === 'channel' && !isBreakpointSm) {
          setTimeout(() => focusChannelComposer(), 50);
        }

        // setTimeout lets tiptap finish init, refs avoid stale closures
        setTimeout(() => {
          const draftContent = savedDraftContentRef.current;
          const persistenceId = draftPersistenceIdRef.current;
          if (draftContent && persistenceId && !editor.isDestroyed) {
            editor.commands.setContent(draftContent);
          }
        }, 0);
      },
      editorProps: {
        attributes: {
          // TODO: make a colorway for the composer and add it here
          // MESSAGE_CONTENT_TYPOGRAPHY_CLASS keeps the input text identical to rendered bubble text.
          // cn, not interpolation: a caller passing its own text size has to win the merge
          // rather than land beside the default and lose to stylesheet order.
          class: cn('richdown-content--base', MESSAGE_CONTENT_TYPOGRAPHY_CLASS, 'max-w-none focus:outline-none', className),
          enterkeyhint: 'send',
        },
        handlePaste: (view, event) => {
          const shouldNormalizeHtmlPaste = pastedHtmlExists(event.clipboardData?.getData('text/html'));
          if (handleFilePasteEvent(event)) return true;
          const handled = handlePasteOverrides(view, event);
          if (shouldNormalizeHtmlPaste) scheduleHtmlPasteNormalization(view);
          return handled;
        },
      },
    });

    // placeholder can change without an editor state change (e.g. channel rename)
    // but tiptap only recomputes decorations on transactions, so we poke it with
    // a no-op transaction. we picked addToHistory:false because this should stay
    // a no-op while still triggering the decoration recompute we want
    // the ref + function in placeholderExtension ensures it reads the fresh value
    useEffect(() => {
      if (!editor || editor.isDestroyed) return;
      editor.view.dispatch(editor.state.tr.setMeta('addToHistory', false));
    }, [editor, placeholder]);

    // ─────────────────────────────────────────────────────────────────────────
    // Send button click
    // ─────────────────────────────────────────────────────────────────────────
    // Button bypasses structural node checks - user explicitly wants to send.
    // Button is disabled when !ableToSend, so this is just a safety check.
    const submitForm = useCallback(() => {
      if (!editor) return;
      if (!ableToSendRef.current) return;
      performSubmission(editor);
    }, [editor, performSubmission]);

    // Clears reply mode, the composer content, and attachments (used by both Escape key and close button)
    const handleClearReply = useCallback(() => {
      editor?.commands.clearContent();
      cleanupUploadsForSessionKey(composeSessionKeyRef.current);
      onClearReply?.();
    }, [editor, cleanupUploadsForSessionKey, onClearReply]);

    // Manage entering/exiting edit mode: swap session keys, focus editor, and clear lingering uploads.
    useEffect(() => {
      const previousId = previousEditingMessageIdRef.current;
      const wasEditing = Boolean(previousId);
      const isEditing = Boolean(editingMessageId);

      if (isEditing && editingMessageId !== previousId) {
        logDebug('RichdownComposer', 'Entering mobile style editing mode', { editingMessageId, editorId });
        // Skip autofocus on mobile - it can trigger unwanted scroll behavior
        if (editor && !isBreakpointSm) {
          editor.commands.focus();
        }

        if (previousId) {
          const previousSessionKey = activeEditSessionKeyRef.current;
          if (previousSessionKey) cleanupUploadsForSessionKey(previousSessionKey);
        }

        const nextSessionKey = generateEditSessionKey(editingMessageId);
        activeEditSessionKeyRef.current = nextSessionKey;
        activeSessionKeyRef.current = nextSessionKey;
        setActivesessionKey(nextSessionKey);
      } else if (!isEditing && wasEditing) {
        logDebug('RichdownComposer', 'Exiting mobile style editing mode', { editorId });
        const previousSessionKey = activeEditSessionKeyRef.current;
        if (previousSessionKey) cleanupUploadsForSessionKey(previousSessionKey);
        activeEditSessionKeyRef.current = null;
        activeSessionKeyRef.current = composeSessionKeyRef.current;
        setActivesessionKey(composeSessionKeyRef.current);
      }

      // Track the current edit target so next pass can detect transitions.
      previousEditingMessageIdRef.current = editingMessageId;
    }, [cleanupUploadsForSessionKey, editingMessageId, editor, editorId, generateEditSessionKey, isBreakpointSm]);

    // Mirror the latest message state into TipTap so it reflects server truth on every edit switch.
    useEffect(() => {
      if (!editor) return;

      const previousMessage = previousEditingMessageRef.current;
      const previousMessageId = previousMessage?.id;
      const currentMessageId = editingMessage?.id;

      const contentChanged =
        editingMessage && (!previousMessage || currentMessageId !== previousMessageId || editingMessage.text !== previousMessage.text);

      if (contentChanged) {
        const content = editingMessage.text || '';
        editor.commands.setContent(content);
        const isEmpty = isEditorContentEmpty(editor);
        setHasTextContent(!isEmpty);
        onEmptyStateChange?.(isEmpty);
        onChange?.(content);
      } else if (!editingMessage && previousMessage) {
        editor.commands.clearContent();
        setHasTextContent(false);
        onEmptyStateChange?.(true);
        onChange?.('');
      }

      previousEditingMessageRef.current = editingMessage || null;
    }, [editingMessage, editor, onEmptyStateChange, onChange]);

    // Hydrate existing attachments into the upload manager when entering edit mode.
    useEffect(() => {
      if (!editingMessageId) {
        lastPrepopulatedAttachmentsRef.current = { sessionKey: null, signature: null };
        return;
      }

      const sessionKey = activeEditSessionKeyRef.current;
      if (!sessionKey) return;

      const attachments = editingMessage?.attachments || [];
      const signature = attachments.map((attachment) => attachment?.id || '').join('|');

      // Only hydrate attachments if this session/signature combo hasn't been synced yet.
      const lastPrepopulated = lastPrepopulatedAttachmentsRef.current;
      if (lastPrepopulated.sessionKey === sessionKey && lastPrepopulated.signature === signature) return;

      let cancelled = false;

      syncExistingMediaAttachments({
        attachments,
        sessionKey,
        channelId: editorId,
      })
        .then(() => {
          if (cancelled) return;
          lastPrepopulatedAttachmentsRef.current = { sessionKey, signature };
        })
        .catch((error) => {
          if (cancelled) return;
          logWarn('RichdownComposer', 'Failed to sync existing attachments for session', {
            sessionKey,
            error,
          });
        });

      return () => {
        cancelled = true;
      };
    }, [editingMessage, editingMessageId, editorId]);

    useEffect(() => {
      if (!editor || !isFocused || !variant) return;
      setActiveComposer(variant);
    }, [isFocused, editor, variant, setActiveComposer]);

    // Manages draft content persistence and synchronization.
    // When draftPersistenceId changes, loads the appropriate saved draft content or clears the editor.
    // When staying on the same draft but editor is not focused, syncs content with saved state.
    // Prevents overwriting content while user is actively typing (isFocused check).
    useEffect(() => {
      if (!editor || draftPersistenceId === '') {
        if (draftPersistenceId === '') {
          previousPersistenceIdRef.current = draftPersistenceId;
        }
        return;
      }

      const handleUpdate = () => {
        const html = editor.getHTML();
        const isEmpty = isEditorContentEmpty(editor);

        // only save non-empty. tiptap fires spurious empty updates during setContent
        // that would nuke restored drafts. check editor.isFocused to distinguish user actions
        // from programmatic updates.
        if (!isEmpty) {
          setDraft(draftPersistenceId, html);
        } else if (isEditorViewMounted(editor) && editor.isFocused) {
          // user manually deleted all content - clear the draft
          clearDraft(draftPersistenceId);
        }
      };

      // register before any setContent calls
      editor.on('update', handleUpdate);

      // channel switching (onCreate handles initial mount)
      const previousId = previousPersistenceIdRef.current;
      const currentHtml = editor.getHTML();
      const isInitialMount = previousId === null;

      if (previousId !== draftPersistenceId) {
        previousPersistenceIdRef.current = draftPersistenceId;

        // skip initial mount (onCreate handles it), only do channel switches
        if (!isInitialMount) {
          if (savedDraftContent) {
            editor.commands.setContent(savedDraftContent);
          } else {
            editor.commands.clearContent();
          }
        }
      } else if (!isFocused) {
        // sync from localStorage when unfocused (e.g. another tab changed draft)
        if (savedDraftContent && savedDraftContent !== currentHtml) {
          editor.commands.setContent(savedDraftContent);
        } else if (!savedDraftContent) {
          if (hasEditorContent(editor)) {
            editor.commands.clearContent();
          }
        }
      }

      return () => {
        editor.off('update', handleUpdate);
      };
    }, [editor, draftPersistenceId, savedDraftContent, isFocused]);

    // Register the editor with the RichdownComposerFocusRouter context
    useEffect(() => {
      if (!editor || !variant) return;

      register(variant, editor, ref);
      onReady?.();

      return () => unregister(variant);
    }, [editor, variant, ref, register, unregister, onReady]);

    const updateToolbarState = useCallback(
      (editorInstance) => {
        if (!editorInstance) return;

        const editorState = {
          editorId,
          isBold: editorInstance.isActive('bold'),
          isItalic: editorInstance.isActive('italic'),
          isStrike: editorInstance.isActive('strike'),
          isCode: editorInstance.isActive('code'),
          isBlockquote: editorInstance.isActive('blockquote'),
          isCodeBlock: editorInstance.isActive('codeBlock'),
          isBulletList: editorInstance.isActive('bulletList'),
          isOrderedList: editorInstance.isActive('orderedList'),
          isLink: editorInstance.isActive('link'),
          hasTextSelection: !editorInstance.state.selection.empty,
        };

        window.dispatchEvent(new CustomEvent('richdown-composer:state-change', { detail: editorState }));
      },
      [editorId],
    );

    useEffect(() => {
      if (!editor) return;

      const handleEditorCommand = (event) => {
        const { action, options, targetEditorId } = event.detail;

        if (targetEditorId !== editorId) return;

        if (action && editor) {
          try {
            switch (action) {
              case 'submit':
                submitForm();
                break;
              case 'toggleBold':
                editor.chain().focus().toggleBold().run();
                break;
              case 'toggleItalic':
                editor.chain().focus().toggleItalic().run();
                break;
              case 'toggleStrike':
                editor.chain().focus().toggleStrike().run();
                break;
              case 'toggleCode':
                editor.chain().focus().toggleCode().run();
                break;
              case 'toggleBlockquote':
                editor.chain().focus().toggleBlockquote().run();
                break;
              case 'toggleCodeBlock':
                editor.chain().focus().toggleCodeBlock().run();
                break;
              case 'toggleBulletList':
                editor.chain().focus().toggleBulletList().run();
                break;
              case 'toggleOrderedList':
                editor.chain().focus().toggleOrderedList().run();
                break;
              case 'setLink':
                editor.chain().focus().extendMarkRange('link').setLink(options).run();
                break;
              case 'unsetLink':
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                break;
              case 'focus':
                editor.commands.focus();
                break;
              case 'insertEmoji':
                editor.commands.focus();
                if (options.name) {
                  editor.commands.insertEmoji({ custom: options.custom, name: options.name, image: options.image });
                } else {
                  editor.commands.insertContent(options.emoji);
                }
                break;
              default:
                logWarn('RichdownComposer', `Unknown editor command: ${action}`);
            }

            updateToolbarState(editor);
          } catch (e) {
            logError('RichdownComposer', 'Error executing editor command:', e);
          }
        }
      };

      window.addEventListener('richdown-composer:command', handleEditorCommand);
      updateToolbarState(editor);
      return () => {
        window.removeEventListener('richdown-composer:command', handleEditorCommand);
      };
    }, [editor, editorId, submitForm, updateToolbarState]);

    const enqueueFilesForUpload = useCallback(
      async (files) => {
        // Check if at max capacity before processing any files
        if (isAtMaxCapacity) {
          logDebug('RichdownComposer', 'File upload rejected - already at max capacity');
          return;
        }

        // Limit files to remaining attachment slots
        const filesToProcess = remainingAttachmentSlots !== undefined ? files.slice(0, remainingAttachmentSlots) : files;

        // determine the purpose based on the current context
        const purpose = editorId?.startsWith('agent-chat-') ? 'agent_chat_message_attachments' : 'message_attachments';

        for (const file of filesToProcess) {
          try {
            await enqueueUpload({
              file,
              purpose,
              workspaceId,
              sessionKey: activeSessionKeyRef.current,
              channelId: editorId,
              ...draftOwnerOptions,
            });
          } catch (error) {
            logError('RichdownComposer', 'Failed to enqueue upload', error);
          }
        }
      },
      [draftOwnerOptions, editorId, isAtMaxCapacity, remainingAttachmentSlots, workspaceId],
    );

    // Shared handler for file pastes - used by both handlePaste and forcePaste
    const handleFilePaste = useCallback(
      (files) => {
        if (!files?.length) return false;

        // Check if pasting more files than remaining slots allow
        const remaining = remainingAttachmentSlotsRef.current;
        if (remaining !== undefined && files.length > remaining) {
          logWarn('RichdownComposer', 'Paste exceeds attachment limit', {
            pastedCount: files.length,
            remainingSlots: remaining,
            wouldExceedBy: files.length - remaining,
          });
          setAttachmentLimitExceeded(true);
        }

        enqueueFilesForUpload(files);
        return true;
      },
      [enqueueFilesForUpload],
    );

    const handleFilePasteEvent = useCallback(
      (event) => {
        const files = Array.from(event.clipboardData?.files || []);
        if (!files.length) return false;

        event.preventDefault();
        handleFilePaste(files);
        return true; // handled here so other paste paths can bail early
      },
      [handleFilePaste],
    );

    const handlePasteOverrides = useCallback((view, event, plainTextOverride = null) => {
      // Shared paste quirks for both native and routed paste paths.
      // Check for Twitter-style URL pastes (styled spans with pre-wrap) and handle as plain text
      const clipboardHtml = event.clipboardData?.getData('text/html');
      const clipboardText = event.clipboardData?.getData('text/plain');
      if (looksLikeTwitterUrlPaste(clipboardHtml, clipboardText)) {
        event.preventDefault();
        const { state, dispatch } = view;
        const textNode = state.schema.text(clipboardText);
        dispatch(state.tr.replaceSelectionWith(textNode, false));
        return true;
      }

      // Check for HTML content that looks like code (from IDEs like VS Code)
      // This is mostly copied over from the tiptap code block extension
      // https://github.com/ueberdosis/tiptap/blob/main/packages/extension-code-block/src/code-block.ts#L425
      const rawHtml = clipboardHtml;
      const plainText = plainTextOverride ?? clipboardText;
      const isCodeLikePaste = rawHtml && plainText && looksLikeCodePaste(rawHtml);

      if (isCodeLikePaste && isSingleLinePaste(plainText)) {
        event.preventDefault();
        // Single-line code paste: keep inline code instead of forcing a full code block.
        const { state, dispatch } = view;
        const { schema } = state;
        const codeMarkType = schema.marks.code;

        if (codeMarkType) {
          const textNode = schema.text(plainText, [codeMarkType.create()]);
          const tr = state.tr.replaceSelectionWith(textNode, false);
          dispatch(tr);
          return true;
        }
      }

      if (isCodeLikePaste && plainText && !isSingleLinePaste(plainText)) {
        event.preventDefault();
        // Multi-line code paste from IDEs without <pre>: treat as a code block.
        const { state, dispatch } = view;
        const { schema } = state;
        const codeBlockType = schema.nodes.codeBlock;

        if (codeBlockType) {
          const textNode = schema.text(plainText.replace(/\r\n?/g, '\n'));
          const tr = state.tr.replaceSelectionWith(codeBlockType.create({}, textNode));
          dispatch(tr);
          return true;
        }
      }

      return false;
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getEditor: () => editor,
        // Called by RichdownComposerFocusRouter when user pastes while focus is outside the editor.
        // Replicates native paste behavior so plugins and rich text formatting work correctly.
        forcePaste: (event) => {
          // Keep the same ordering as handlePaste: files -> overrides -> plugins -> fallback.
          // 1. File attachments take priority
          if (handleFilePasteEvent(event)) return;

          if (editor) {
            event.preventDefault();
            editor.commands.focus();
            const shouldNormalizeHtmlPaste = pastedHtmlExists(event.clipboardData?.getData('text/html'));

            // 2. Handle paste overrides (twitter URLs, inline code, etc.)
            const plainTextOverride = event.clipboardData?.getData('text/plain') || event.clipboardData?.getData('text');
            if (handlePasteOverrides(editor.view, event, plainTextOverride)) {
              if (shouldNormalizeHtmlPaste) scheduleHtmlPasteNormalization(editor.view);
              return;
            }

            // 3. Delegate to ProseMirror plugin handlers (e.g., CodeBlock's VS Code detection).
            //    someProp iterates all plugins' handlePaste props until one returns true.
            const handled = editor.view.someProp('handlePaste', (handler) => handler(editor.view, event, Slice.empty));
            if (handled) {
              if (shouldNormalizeHtmlPaste) scheduleHtmlPasteNormalization(editor.view);
              return;
            }

            // 4. Fallback: handle rich text ourselves
            const rawHtml = event.clipboardData?.getData('text/html');
            const html = rawHtml ? sanitizePastedHtml(rawHtml) : null;
            const plainText = plainTextOverride;

            // 5. Default: use ProseMirror's schema-aware parsing to handle pastes with markup
            if (html) {
              const { state, dispatch } = editor.view;
              const dom = document.createElement('div');
              dom.innerHTML = html;
              const slice = DOMParser.fromSchema(state.schema).parseSlice(dom);
              dispatch(state.tr.replaceSelection(slice));
              if (shouldNormalizeHtmlPaste) scheduleHtmlPasteNormalization(editor.view);
            } else if (plainText) {
              editor.commands.insertContent(plainText);
            }
          }
        },
        setMarkdown: (content) => {
          if (editor) {
            editor.commands.setContent(content);
            onChange?.(content);
          }
        },
        clear: () => {
          if (editor) {
            editor.commands.clearContent();
            onChange?.('');
          }
        },
      }),
      [editor, onChange, handleFilePasteEvent, handlePasteOverrides],
    );

    useEffect(() => {
      if (editor && initialContent) {
        editor.commands.setContent(initialContent);
        onChange?.(initialContent);
        updateToolbarState(editor);
      }
    }, [initialContent, editor, onChange, updateToolbarState]);

    useEffect(() => {
      if (!editor) return;

      const onSelectionUpdate = () => {
        updateToolbarState(editor);
      };

      editor.on('selectionUpdate', onSelectionUpdate);

      return () => {
        editor.off('selectionUpdate', onSelectionUpdate);
      };
    }, [editor, updateToolbarState]);

    const mountTimeRef = useRef(Date.now());

    // Skip autofocus on mobile - it can trigger unwanted scroll behavior
    useEffect(() => {
      if (isBreakpointSm) return;
      const timeSinceMount = Date.now() - mountTimeRef.current;
      if (timeSinceMount > 1000 && editor) editor.commands.focus();
    }, [editor, isBreakpointSm]);

    // Focus editor when entering reply mode
    useEffect(() => {
      if (replyToMessageId && editor) {
        editor.commands.focus();
      }
    }, [replyToMessageId, editor]);

    const uploadStates = useMemo(() => {
      return composerMediaAttachments.map((m) => m.upload.state);
    }, [composerMediaAttachments]);

    const allUploadsComplete = useMemo(() => {
      return uploadStates.every((state) => state === 'completed');
    }, [uploadStates]);

    const hasUploadErrors = useMemo(() => {
      return uploadStates.some((state) => state === 'error');
    }, [uploadStates]);

    const canSubmitWithPendingUploads = variant === 'channel' && !editingMessageId;

    // ─────────────────────────────────────────────────────────────────────────
    // Is the Send button enabled?
    // ─────────────────────────────────────────────────────────────────────────
    // Can send when:
    // - Has text or uploads (or editing mode with empty content = delete)
    // - Channel sends can fire while uploads are still going (optimistic message takes over);
    //   edit/agent chat wait for resolved attachment ids
    // - Content is within size limits and composer is not disabled
    const ableToSend = useMemo(() => {
      if (disabled) return false;
      if (isRichdownContentTooLarge) return false;

      if (hasUploads && hasUploadErrors) return false;
      if (!canSubmitWithPendingUploads && hasUploads && !allUploadsComplete) return false;

      // In edit mode we allow submitting an empty message so the footer can
      // optionally route that action to delete with confirmation.
      if (editingMessageId && !hasTextContent && !hasUploads) return true;

      if (hasTextContent) return true;

      if (hasUploads) return canSubmitWithPendingUploads || allUploadsComplete;

      // make sure an message with no text and no uploads but at least one existing attachment can be submitted
      return false;
    }, [
      allUploadsComplete,
      disabled,
      editingMessageId,
      hasTextContent,
      hasUploadErrors,
      hasUploads,
      isRichdownContentTooLarge,
      canSubmitWithPendingUploads,
    ]);

    const dropzoneDisabled = false; // Never disable the dropzone itself, we'll handle rejection in callbacks

    const onDropAccepted = useCallback(
      (acceptedFiles) => {
        if (!acceptedFiles?.length) return;

        // Ignore drops when at max capacity
        if (isAtMaxCapacity) {
          logDebug('RichdownComposer', 'Drop ignored - already at max capacity');
          setAttachmentLimitExceeded(true);
          return;
        }
        // Show banner if selecting more files than remaining slots allow
        if (remainingAttachmentSlots !== undefined && acceptedFiles.length > remainingAttachmentSlots) {
          setAttachmentLimitExceeded(true);
        }
        enqueueFilesForUpload(acceptedFiles);
        if (editor) editor.commands.focus();
      },
      [enqueueFilesForUpload, editor, isAtMaxCapacity, remainingAttachmentSlots],
    );

    const onDropRejected = useCallback((rejections) => {
      if (!rejections?.length) return;

      rejections.forEach(({ file, errors = [] }) => {
        errors.forEach((error) => {
          logWarn('RichdownComposer', 'File rejected by dropzone', {
            fileName: file?.name,
            code: error?.code,
            message: error?.message,
          });
        });
      });
    }, []);

    const dropzoneState = useMemo(
      () => ({
        disabled: dropzoneDisabled,
        onDrop: onDropAccepted,
        onDropRejected,
      }),
      [dropzoneDisabled, onDropAccepted, onDropRejected],
    );

    useEffect(() => {
      if (onDropzoneStateChange) {
        onDropzoneStateChange(dropzoneState);
      }
    }, [dropzoneState, onDropzoneStateChange]);

    useEffect(() => {
      return () => {
        if (onDropzoneStateChange) onDropzoneStateChange(null);
      };
    }, [onDropzoneStateChange]);

    const dropzoneGetInputProps = useMemo(() => {
      if (dropzoneHelpers?.getInputProps) return dropzoneHelpers.getInputProps;
      return (props = {}) => props;
    }, [dropzoneHelpers]);

    // Keep some refs in sync with real useStates so we can access them in handlers.
    performSubmissionRef.current = performSubmission;
    ableToSendRef.current = ableToSend;
    draftPersistenceIdRef.current = draftPersistenceId;
    savedDraftContentRef.current = savedDraftContent;
    submitHandlerRef.current = onSubmit;
    remainingAttachmentSlotsRef.current = remainingAttachmentSlots;

    const inEditMode = useMemo(() => editingMessageId !== null, [editingMessageId]);

    // The send button only appears once there's something to send (edit mode always shows it)
    const showSendButton = inEditMode || hasTextContent || hasUploads;

    // The reply target row is retained when Reply is chosen; a fresh profile row is
    // preferred for the banner, with the row's author snapshot as the offline fallback.
    const replyToAuthor = useMessageAuthor(replyToMessage, workspaceId);

    const defaultContainerClassName = 'relative flex w-full cursor-text flex-col richdown-composer-wrapper rounded-3xl';

    return (
      <div
        className={containerClassName || defaultContainerClassName}
        data-mobile-style-editing={inEditMode ? 'true' : 'false'}
        data-mobile-style-editing-message-id={editingMessageId || undefined}
      >
        {inEditMode && <ComposerModeBar label="Edit message" onClose={handleCancelEditing} closeAriaLabel="Exit edit mode" />}

        {replyToMessageId && (
          <ComposerModeBar
            label={`Replying to ${replyToAuthor?.full_name || 'a message'}`}
            onClose={handleClearReply}
            closeAriaLabel="Cancel reply"
          />
        )}

        {attachmentLimitExceeded && (
          <div className="mx-3 mt-3 flex items-center gap-2.5 rounded-3xl bg-neutral-950/80 px-4 py-2 text-cursed leading-5 font-medium text-white">
            <span>
              You can only upload {maxFiles} file{maxFiles === 1 ? '' : 's'} at a time
            </span>
            <button
              type="button"
              className="ml-auto flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-sm hover:bg-white/10"
              onClick={handleDismissAttachmentLimit}
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        )}

        <RichdownComposerMediaAttachments
          channelId={editorId}
          sessionKey={activeSessionKey}
          roundedTop={!inEditMode && !replyToMessageId}
        />

        {/* One vertical rhythm whether or not the toolbar shows: 6px around a 36px row.
            Hiding the toolbar only removes buttons — it should not make the composer a
            different size from the one in a channel. */}
        <div className={cn('flex items-end', dense ? 'p-1' : 'p-[6px]', hideToolbar ? 'gap-1' : 'gap-[20px]')}>
          <div className={cn('flex min-w-0 flex-1 items-end', hideToolbar ? 'gap-1' : 'gap-[6px]')}>
            {/* Placeholder keeps vertical alignment consistent when composer actions are
                hidden, and its width is the text's left inset — with no buttons there,
                a hairline left the first character flush against the pill. */}
            {hideToolbar ? (
              <div className="h-9 w-2" />
            ) : (
              <ComposerActions
                setEmojiButtonRef={emojibarRefs.setReference}
                editorId={editorId}
                onEmojibarToggle={handleEmojibarToggle}
                onAttachmentClick={handleAttachmentButtonClick}
                isAtMaxCapacity={isAtMaxCapacity}
              />
            )}

            {isEmojibarOpen && (
              <FloatingPortal>
                <div ref={emojibarRefs.setFloating} style={emojibarFloatingStyles} className="z-(--stack-emoji-picker)">
                  <div ref={emojibarContentRef}>
                    <EmojibarPopover onEmojiSelect={handleEmojibarSelect} onGifSelect={handleGifSelect} />
                  </div>
                </div>
              </FloatingPortal>
            )}

            {/* Bottom padding keeps the last line of text aligned with the center of the buttons, even as the textarea grows */}
            {/* Symmetric padding sized so a single line totals exactly the 36px button
                height, which is what centers it against the buttons: 7+1+20+1+7 at the
                default 20px line-height, 8+1+18+1+8 at the dense 18px one. Change the
                line-height without changing this and the text stops being centered. */}
            <div className={cn('relative min-w-0 flex-1', dense ? 'py-2' : 'py-[7px]')}>
              <input {...dropzoneGetInputProps({ multiple: true })} className="hidden" />
              {/* Match the single-line footprint so the empty placeholder doesn't shrink and trigger a list resize. */}
              {/* -ml/pl pair widens the clip window leftward so outside-positioned list markers aren't sliced, without moving the text */}
              <div className="-ml-3 max-h-40 overflow-y-auto py-px pl-3">
                <EditorContent editor={editor} data-richdown-composer-wrapper="true" />
              </div>
              {/* Portal-rendered, so the overflow clip above can't slice it (what killed the old BubbleMenu). */}
              <ComposerSelectionToolbar editor={editor} />
            </div>
          </div>

          {submitButton ? (
            // pass the relevant props to the external submit button component
            submitButton({ editor, onSubmit: submitForm, ableToSend, inEditMode })
          ) : (
            // render the default submit button, which handles edit mode.
            // hidden (but still occupying space, per design) until there's something to send
            <RichdownButton
              editor={editor}
              className={cn(
                'flex items-center justify-center transition-[opacity,scale] duration-150',
                !ableToSend ? 'bg-gray-400 dark:bg-neutral-700' : 'bg-send-blue hover:bg-[#0077df]',
                !showSendButton && 'pointer-events-none scale-75 opacity-0',
              )}
              onClick={submitForm}
              disabled={!ableToSend}
              ariaLabel={inEditMode ? 'Save message' : 'Send message'}
            >
              <SendMessageIcon className={`size-4 ${!ableToSend ? 'text-white dark:text-neutral-400' : 'text-white'}`} />
            </RichdownButton>
          )}
        </div>
      </div>
    );
  },
);

RichdownComposer.displayName = 'RichdownComposer';

export default RichdownComposer;
